import {
  createStore,
  combineReducers,
  applyMiddleware,
  AnyAction,
  Middleware,
  Reducer,
} from 'redux'
import thunk from 'redux-thunk'
import promise from 'redux-promise'
import { createLogger } from 'redux-logger'

import { reduceReducers } from './services/redux'

import {
  settings,
  auth,
  context,
  templates,
  currentUser,
  crmSyncSettings,
  teamEmailSettings,
  teamPrivacySettings,
  fieldOrders,
  crmReferences,
  sentiments,
  calendar,
  trackingPreferences,
  initialState as rootInitialState,
} from './reducers'
import snippets from './redux/snippets'
import notifications from './redux/notifications'
import customFields from './redux/customFields'
import requiredFields from './redux/requiredFields'
import cadences from './redux/cadences'
import reminders from './redux/reminders'
import accounts from './redux/accounts'
import tags from './redux/tags'
import groups from './redux/groups'
import templatesReducer from './redux/templates'
import { RESET_STORE } from './redux/root'
import { watchSentryUser } from './sentry-config'
import office from './office/office'
import gateway from './gateway/gateway'

const middlewares: Middleware[] = [
  gateway.watchToken,
  watchSentryUser,
  thunk.withExtraArgument({ office, gateway }),
  promise,
]

if (process.env.NODE_ENV === 'development') {
  middlewares.push(createLogger({ collapsed: true }) as Middleware)
}

const appReducer = combineReducers({
  settings,
  auth,
  context,
  tags,
  cadences,
  templates: reduceReducers<any, AnyAction>(
    rootInitialState.templates,
    templates,
    templatesReducer as Reducer<any, AnyAction>
  ),
  snippets,
  reminders,
  notifications,
  currentUser,
  customFields,
  requiredFields,
  crmSyncSettings,
  teamEmailSettings,
  teamPrivacySettings,
  fieldOrders,
  crmReferences,
  accounts,
  groups,
  sentiments,
  calendar,
  trackingPreferences,
})

type AppState = ReturnType<typeof appReducer>

const initialState = appReducer(undefined, { type: '@@INIT' } as AnyAction)

const rootReducer: Reducer<AppState, AnyAction> = (state, action) => {
  if (action.type === RESET_STORE) {
    return appReducer(initialState, action)
  }

  return appReducer(state, action)
}

export const createInitialStore = () => createStore(rootReducer, applyMiddleware(...middlewares))

const store = createInitialStore()

export default store
